import React, { useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { Image } from "expo-image";

import OnboardingBaseScreen from "./Components/OnboardingBaseScreen";
import OnboardingWelcomeScreenBaseItem from "./Components/WelcomeScreen/OnboardingWelcomeScreenBaseItem";

import { UserSeekingAttributeSet } from "@/schemas/profile/attributes/UserSeekingAttributeSet";
import type { AppUserSession } from "@/schemas/auth/session";
import { themes } from "@/Components/Styling/themeConfigs";
import { Spacings } from "@/styling/spacings";
import { UserAttributeType } from "@/schemas/profile/UserAttributeSet";
import type { AttributeValue } from "@/schemas/attribute/AttributeSet";
import { welcomeScreenSeekingCardOptions } from "@/utils/onboarding";

type Props = {
  session: AppUserSession;
};

function OnboardingWelcomeScreen({ session }: Props): React.JSX.Element {
  const [selectedCardIds, setSelectedCardIds] = useState<number[]>([]);

  const isCardSelected = (id: number): boolean => {
    return selectedCardIds.includes(id);
  };

  const handleCardPress = (id: number): void => {
    setSelectedCardIds((prev) =>
      isCardSelected(id)
        ? prev.filter((cardId) => cardId !== id)
        : [...prev, id],
    );
  };

  // Function to collect all unique seeking attributes from selected cards
  const getSelectedSeekingValues = (): AttributeValue[] => {
    // Returns all selected seeking AttributeValue objects for the chosen cards
    const selectedSeekingAttributes = selectedCardIds.flatMap((id) => {
      const card = welcomeScreenSeekingCardOptions.find(
        (option) => option.id === id,
      );
      return card ? card.seekingAttributes : [];
    });

    const uniqueSeekingAttributes = Array.from(
      new Set(selectedSeekingAttributes),
    );

    return uniqueSeekingAttributes
      .map((id) => UserSeekingAttributeSet.map[id])
      .filter((v): v is AttributeValue => v !== undefined);
  };

  const aboveTitleComponent: React.JSX.Element = (
    <Image
      source={require("@/assets/favicon.png")}
      style={styles.appIcon}
      contentFit="contain"
      accessibilityLabel="Kai Social Logo"
    />
  );

  const stageMainComponent: React.JSX.Element = (
    <>
      <Text style={styles.purposeTitle}>What brings you here?</Text>
      <View>
        {welcomeScreenSeekingCardOptions.map((card) => {
          const isSelected = isCardSelected(card.id);

          return (
            <Pressable key={card.id} onPress={() => handleCardPress(card.id)}>
              <OnboardingWelcomeScreenBaseItem
                icon={card.icon}
                title={card.title}
                description={card.description}
                selected={isSelected}
              />
            </Pressable>
          );
        })}
      </View>
    </>
  );

  return (
    <OnboardingBaseScreen
      session={session}
      aboveTitleComponent={aboveTitleComponent}
      title={"Welcome to Kai Social"}
      subtitle={"Your safe space for authentic connections"}
      canContinue={selectedCardIds.length > 0}
      stageMainComponent={stageMainComponent}
      attributeType={UserAttributeType.SEEKING}
      attributeSelections={getSelectedSeekingValues()}
    />
  );
}

const currentTheme = themes.morning;
const styles = StyleSheet.create({
  appIcon: {
    width: 100,
    height: 100,
    alignItems: "center",
    marginBottom: Spacings.s2,
  },
  purposeTitle: {
    fontSize: currentTheme.typography.h1.size.mobile,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: Spacings.s3,
  },
});

export default OnboardingWelcomeScreen;
